import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";

class FirebaseAuthBackend {
  constructor(firebaseConfig) {
    if (firebaseConfig) {
      this.app = initializeApp(firebaseConfig);
      this.auth = getAuth(this.app);
      this.db = getFirestore(this.app);

      // Persist user in localStorage
      onAuthStateChanged(this.auth, (user) => {
        if (user) {
          localStorage.setItem("authUser", JSON.stringify(user));
        } else {
          localStorage.removeItem("authUser");
        }
      });
    }
  }

  /**
   * Register user
   */
  registerUser = (email, password) => {
    return new Promise((resolve, reject) => {
      createUserWithEmailAndPassword(this.auth, email, password)
        .then(() => resolve(this.auth.currentUser))
        .catch((error) => reject(this._handleError(error)));
    });
  };

  /**
   * Edit profile (same as register, you can update later)
   */
  editProfileAPI = (email, password) => {
    return new Promise((resolve, reject) => {
      createUserWithEmailAndPassword(this.auth, email, password)
        .then(() => resolve(this.auth.currentUser))
        .catch((error) => reject(this._handleError(error)));
    });
  };

  /**
   * Login user
   */
  loginUser = (email, password) => {
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(this.auth, email, password)
        .then(() => resolve(this.auth.currentUser))
        .catch((error) => reject(this._handleError(error)));
    });
  };

  /**
   * Forgot password
   */
  forgetPassword = (email) => {
    return new Promise((resolve, reject) => {
      sendPasswordResetEmail(this.auth, email, {
        url: window.location.protocol + "//" + window.location.host + "/login",
      })
        .then(() => resolve(true))
        .catch((error) => reject(this._handleError(error)));
    });
  };

  /**
   * Logout user
   */
  logout = () => {
    return new Promise((resolve, reject) => {
      signOut(this.auth)
        .then(() => resolve(true))
        .catch((error) => reject(this._handleError(error)));
    });
  };

  /**
   * Add new user to Firestore
   */
  addNewUserToFirestore = async (user) => {
    if (!user?.uid) return;

    const { profile } = user.additionalUserInfo || {};
    const details = {
      firstName: profile?.given_name || profile?.first_name || "",
      lastName: profile?.family_name || profile?.last_name || "",
      fullName: profile?.name || "",
      email: profile?.email || user.email,
      picture: profile?.picture || "",
      createdDtm: serverTimestamp(),
      lastLoginTime: serverTimestamp(),
    };

    await setDoc(doc(this.db, "users", user.uid), details);
    return { user, details };
  };

  /**
   * Store user in localStorage
   */
  setLoggeedInUser = (user) => {
    localStorage.setItem("authUser", JSON.stringify(user));
  };

  /**
   * Get authenticated user
   */
  getAuthenticatedUser = () => {
    if (!localStorage.getItem("authUser")) return null;
    return JSON.parse(localStorage.getItem("authUser"));
  };

  /**
   * Handle errors
   */
  _handleError(error) {
    return error.message;
  }
}

let _fireBaseBackend = null;

/**
 * Initialize Firebase backend
 */
const initFirebaseBackend = (config) => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend(config);
  }
  return _fireBaseBackend;
};

/**
 * Return Firebase backend
 */
const getFirebaseBackend = () => {
  return _fireBaseBackend;
};

export { getFirebaseBackend, initFirebaseBackend };
